/**
 * Kandidaten-Workflow: Stufe, Verantwortlicher, Priorität, Journal.
 * Endpunkte aus Schritt 2 unter /api/v1/candidate-workflow.
 */

import { CandidateStage, CandidatePriority } from '../constants/candidateStage';

const API_BASE = '/api/v1/candidate-workflow';

export type ActivitySource = 'USER' | 'SYSTEM' | 'IMPORT' | 'EMAIL_SYNC' | 'AUTOMATION' | 'AI';

export interface ActivityEntry {
  id: string;
  user_id: string | null;
  user_name: string | null;
  entity_type: string;
  entity_id: string;
  entity_name: string | null;
  action: string;
  metadata: Record<string, any> | null;
  source: ActivitySource;
  occurred_at: string;
  created_at: string;
}

export interface RecruitingTaskKurz {
  id: string;
  title: string;
  due_at: string | null;
  status: string;
  priority: string;
  task_type: string;
}

export interface CandidateWorkflow {
  stage: CandidateStage;
  stageLabel: string;
  allowedTargets: CandidateStage[];
  priority: CandidatePriority;
  ownerUserId: string | null;
  letzteAktivitaet: ActivityEntry | null;
  naechsteAufgabe: RecruitingTaskKurz | null;
  offeneAufgaben: number;
}

export interface TimelinePage {
  items: ActivityEntry[];
  nextCursor: string | null;
}

async function fetchApi(url: string, options: RequestInit = {}) {
  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });
  const json = await res.json();
  if (!res.ok || !json.success) {
    const fehler = new Error(json?.error || 'Anfrage fehlgeschlagen') as Error & { code?: string };
    fehler.code = json?.code;
    throw fehler;
  }
  return json.data;
}

const candidateWorkflowService = {
  async workflow(candidateId: string): Promise<CandidateWorkflow> {
    return fetchApi(`${API_BASE}/${candidateId}/workflow`);
  },

  async timeline(candidateId: string, params: { limit?: number; before?: string } = {}): Promise<TimelinePage> {
    const qs = new URLSearchParams();
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.before) qs.set('before', params.before);
    const s = qs.toString();
    return fetchApi(`${API_BASE}/${candidateId}/timeline${s ? `?${s}` : ''}`);
  },

  async changeStage(candidateId: string, stage: CandidateStage, grund?: string) {
    return fetchApi(`${API_BASE}/${candidateId}/stage`, {
      method: 'PATCH',
      body: JSON.stringify({ stage, grund }),
    });
  },

  async protokolliere(candidateId: string, typ: string, notiz?: string) {
    return fetchApi(`${API_BASE}/${candidateId}/activities`, {
      method: 'POST',
      body: JSON.stringify({ typ, notiz }),
    });
  },

  async setzeOwner(candidateId: string, ownerUserId: string) {
    return fetchApi(`${API_BASE}/${candidateId}/owner`, {
      method: 'PATCH',
      body: JSON.stringify({ ownerUserId }),
    });
  },

  async setzePrioritaet(candidateId: string, priority: CandidatePriority) {
    return fetchApi(`${API_BASE}/${candidateId}/priority`, {
      method: 'PATCH',
      body: JSON.stringify({ priority }),
    });
  },
};

export default candidateWorkflowService;
